import "./sentry";
import React, {StrictMode} from "react";
import {createRoot} from "react-dom/client";
import App from "./pages/App";
import Authentication from "./pages/auth/Authentication";
import Registration from "./pages/auth/Registration";
import RegistrationConfirm from "./pages/auth/RegistrationConfirm";
import {PasswordChange, RequestAccessLink} from "./pages/auth/ChangePassword";
import CustomRendererSandbox from "./templates";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { Navigate } from "react-router";
import { Provider } from "mobx-react";
import { rootStore } from "./stores";
import {BETA, DEV, getConfig} from "./lib/configMgr";
import GlobalErrorBoundary from "./components/GlobalErrorBoundary";
import OfflineDetect from "./components/OfflineDetect";
import * as Sentry from "@sentry/react";
import AdminPage from "./pages/Admin";
import MaintenancePage from "./pages/MaintenancePage";
import ErrorPage from "./pages/ErrorPage";
import GraphPlayground from "./pages/SigmaGraphPlayground";
import Help from "./pages/Help";
import DocsPage from "./pages/DocsPage";
import Verification from "./pages/auth/Verification";
import CompoundComparisonDemo from "./components/evidence/CompoundComparisonDemo";
import StreamingTestPage from "./pages/StreamingTestPage";
import DeveloperPage from "./pages/developer/DeveloperPage";
import {initTheme} from "./lib/themeManager";
import Analytics from "./lib/Analytics";
export * from "./styles";

const {
  Sentry: sentryOptions,
} = getConfig();

// Handle Vite chunk preload failures (stale cache after deployment) with a one-shot reload
window.addEventListener('vite:preloadError', () => {
  if (!sessionStorage.getItem('chunkReloadAttempted')) {
    sessionStorage.setItem('chunkReloadAttempted', '1');
    window.location.reload();
  }
});

// Suppress benign ResizeObserver errors that don't affect functionality
// These occur when ResizeObserver callbacks trigger faster than the browser can process them
const resizeObserverErrorHandler = (event) => {
  const message = event.message || event.error?.message || '';
  if (message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded')) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
};
window.addEventListener('error', resizeObserverErrorHandler);

// Capture unhandled Promise rejections for PostHog error tracking
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (reason == null) return;
  if (reason?.name === 'SearchCanceled') return;
  const msg = reason?.message || String(reason);
  if (msg.includes('ResizeObserver') ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Importing a module script failed')) return;
  try {
    Analytics.exception(reason instanceof Error ? reason : new Error(msg), false);
  } catch (_) {}
});

// Also suppress via console intercept as last resort
const originalError = console.error;
console.error = (...args) => {
  const message = args[0]?.toString() || '';
  if (message.includes('ResizeObserver loop')) {
    return; // Suppress
  }
  originalError.apply(console, args);
};

const createRouter = sentryOptions.DSN
                     ? Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
                     : createBrowserRouter;


const router = createRouter(
  [
    {
      path: "/",
      element: <App />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/search",
      element: <App />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/demo",
      element: <Navigate to={"/"} replace />,
    },
    {path: "graph", element: DEV ? <GraphPlayground /> : <Navigate to={"/"} replace />},
    {path: "compound-comparison-demo", element: DEV ? <CompoundComparisonDemo /> : <Navigate to={"/"} replace />},
    {path: "help", element: <Help />},
    {path: "docs", element: <DocsPage />},
    {path: "admin", element: <AdminPage />},
    {path: "streaming-test", element: <StreamingTestPage />},
    {path: "signin", element: <Authentication />},
    {path: "verify", element: <Verification />},
    {path: "register", element: <Registration />},
    {path: "registration/confirm", element: <RegistrationConfirm />},
    {path: "changepass", element: <PasswordChange />},
    {path: "resetpass", element: <RequestAccessLink />},
    {path: "sandbox", element: (DEV || BETA) ? <CustomRendererSandbox /> : <Navigate to={"/"} replace />},
    {path: "maintenance", element: <MaintenancePage />},
    {path: "developer", element: <DeveloperPage />},
    {path: "*", element: <Navigate to={"/"} replace />},
  ]
);

// Initialize theme before rendering to prevent flash
initTheme();

const strict_mode = true;
const root = createRoot(document.getElementById('root'));
const content = (
  <GlobalErrorBoundary>
    <Provider {...rootStore} >
      <RouterProvider router={router} />
      <OfflineDetect />
    </Provider>
  </GlobalErrorBoundary>
);

if (strict_mode) {
  root.render(<StrictMode>{content}</StrictMode>);
}
else {
  root.render(content);
}
